import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Landmark, Download, Plus, AlertTriangle, FileText, Trash2, Pencil } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { Panel, Field, FormRow, ErrorState, fmtDate } from './design';

// CHSP grant money: no claim goes to Services Australia; the Department pays a twelfth of the annual
// grant each month in arrears. Record what arrives and see what is missing or short; pacing at each
// quarter end; the year end (delivered against paid, an estimate: the acquittal is on spending); the
// annual financial declaration due 31 August; and any visit funded twice.

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const mon = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;
const STATUS = { paid: ['Paid', 'text-emerald-700'], short: ['Short', 'text-amber-700'], expected: ['Due this month', 'text-slate-600'], missing: ['Missing', 'text-red-800'], 'not yet': ['Not yet', 'text-slate-400'] };
const isoToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: 'blob' });
    const href = window.URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = href; a.download = name; a.click(); window.URL.revokeObjectURL(href);
  } catch (e) {
    let msg = 'Could not download it';
    try { msg = JSON.parse(await e.response.data.text()).message || msg; } catch { /* not JSON */ }
    toast.error(msg);
  }
}

export default function ChspMoney({ fy }) {
  const confirm = useConfirm();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [twice, setTwice] = useState({ visits: [], overlaps: [] });
  const [grant, setGrant] = useState(null);
  const [pay, setPay] = useState(null);       // a new payment being recorded for a month
  const [month, setMonth] = useState(null);   // a month that already has a payment, opened to check it
  const [decl, setDecl] = useState(null);
  const load = useCallback(() => {
    setError(null);
    // A failed load says so: a panel that silently vanished read as "no grant set up".
    api.get('/aged-care/chsp/money', { params: { fy } }).then((r) => setD(r.data))
      .catch((e) => { setD(null); setError(e.response?.data?.message || 'Could not load the grant and payments'); });
    api.get('/aged-care/chsp/double-funding').then((r) => setTwice(r.data || { visits: [], overlaps: [] })).catch(() => {});
  }, [fy]);
  useEffect(() => { load(); }, [load]);
  if (!d) {
    return (
      <Panel title="Grant and payments" icon={Landmark} className="mt-5" bodyClass={error ? '' : 'px-4 py-3 text-[13px] text-slate-500'}>
        {error ? <ErrorState message={error} onRetry={load} /> : 'Loading the grant and payments…'}
      </Panel>
    );
  }
  const saveGrant = async () => {
    try { const r = await api.put('/aged-care/chsp/grant', { fy, annualAmount: grant.annual, agreementRef: grant.ref }); setD(r.data); setGrant(null); toast.success('Saved'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const savePay = async () => {
    try { await api.post('/aged-care/chsp/payments', { fy, ...pay }); setPay(null); toast.success('Payment recorded'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const saveDecl = async () => {
    try { await api.post('/aged-care/chsp/declaration', { fy: decl.fy, submittedOn: decl.on, reference: decl.ref }); setDecl(null); toast.success('Declaration recorded'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const payments = d.payments || [];
  // A month already paid opens what was recorded; clicking it used to add another payment, so the
  // same remittance was easily counted twice. Only a month with nothing in it starts a new one.
  const openMonth = (m) => {
    if (payments.some((p) => p.month === m.month)) { setPay(null); setMonth(m.month); return; }
    setMonth(null);
    setPay({ month: m.month, receivedOn: isoToday(), amount: m.expected, reference: '' });
  };
  const removePay = async (p) => {
    const ok = await confirm(`The ${money(p.amount)} received on ${fmtDate(p.received_on)} for ${mon(p.month)} comes off the schedule, and the month shows as short or missing again.`,
      { title: 'Remove this payment?', confirmLabel: 'Remove payment', danger: true });
    if (!ok) return;
    try { await api.delete(`/aged-care/chsp/payments/${p.id}`); toast.success('Payment removed'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not remove the payment'); }
  };
  const opened = month ? payments.filter((p) => p.month === month) : [];
  const openedRow = month ? d.schedule.find((m) => m.month === month) : null;
  const q = (d.quarters || []).filter((x) => x.ended);
  const y = d.yearEnd;
  return (
    <Panel title="Grant and payments" icon={Landmark} className="mt-5" bodyClass="px-4 py-3 space-y-3 text-[13px]"
      actions={<button type="button" className="btn-ghost btn-sm" onClick={() => download(`/aged-care/chsp/year-end.csv?fy=${encodeURIComponent(fy)}`, `chsp-year-end-${fy}.csv`)}><Download size={13} /> Year-end report</button>}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-slate-700">
          Annual grant <b className="tabular-nums">{money(d.grant.annual)}</b>{d.grant.source === 'targets' ? ' (from your targets × unit prices)' : d.grant.agreementRef ? ` (agreement ${d.grant.agreementRef})` : ''},
          paid {money(d.grant.monthly)} a month in arrears. Received so far <b className="tabular-nums">{money(d.totals.received)}</b> of {money(d.totals.dueSoFar)} due.
        </p>
        {!grant && <button type="button" className="btn-ghost btn-sm" onClick={() => setGrant({ annual: d.grant.source === 'agreement' ? d.grant.annual : '', ref: d.grant.agreementRef || '' })}>Grant from the agreement</button>}
      </div>
      {grant && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-2">
          <Field label="Annual grant ($)" hint="Blank uses targets × unit prices.">{(id) => <input id={id} type="number" min="0" step="0.01" className="input-field !w-40" value={grant.annual} onChange={(e) => setGrant((p) => ({ ...p, annual: e.target.value }))} />}</Field>
          <Field label="Agreement reference">{(id) => <input id={id} className="input-field !w-48" value={grant.ref} onChange={(e) => setGrant((p) => ({ ...p, ref: e.target.value }))} />}</Field>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setGrant(null)}>Cancel</button>
          <button type="button" className="btn-primary btn-sm" onClick={saveGrant}>Save</button>
        </div>
      )}
      {d.totals.missingOrShort > 0 && <p className="text-red-800"><AlertTriangle size={13} className="inline -mt-0.5" /> {money(d.totals.missingOrShort)} missing or short. Follow it up with the Department.</p>}
      <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-1.5">
        {d.schedule.map((m) => {
          const [label, cls] = STATUS[m.status] || ['Not yet', 'text-slate-400'];
          const has = payments.some((p) => p.month === m.month);
          return (
            <button key={m.month} type="button" title={`Paid by ${fmtDate(m.payBy)}`} onClick={() => openMonth(m)}
              aria-label={`${mon(m.month)}: ${label}. ${has ? 'Open the payment recorded' : 'Record the payment'}`}
              aria-pressed={month === m.month || pay?.month === m.month}
              className={`rounded-lg border px-2 py-1.5 text-left hover:bg-slate-50 ${month === m.month || pay?.month === m.month ? 'border-primary-400 ring-1 ring-primary-200' : 'border-slate-200'}`}>
              <span className="block text-[11.5px] font-medium text-slate-700">{mon(m.month)}</span>
              <span className={`block text-[11px] ${cls}`}>{label}</span>
              <span className="block text-[11px] tabular-nums text-slate-500">{m.received ? money(m.received) : '—'}</span>
            </button>
          );
        })}
      </div>
      {opened.length > 0 && (
        <div className="rounded-lg border border-slate-200 p-2 space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-slate-800">Payment{opened.length === 1 ? '' : 's'} for {mon(month)}</span>
            <button type="button" className="btn-ghost btn-sm" onClick={() => setMonth(null)}>Close</button>
          </div>
          {opened.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-slate-700">
                <b className="tabular-nums">{money(p.amount)}</b> received {fmtDate(p.received_on)}
                <span className="text-slate-500">{p.reference ? `, remittance ${p.reference}` : ', no remittance reference'}</span>
              </span>
              <button type="button" className="btn-ghost btn-sm" onClick={() => removePay(p)}><Trash2 size={13} /> Remove</button>
            </div>
          ))}
          {/* A short month can take a second payment for the rest: the Department sometimes pays a
              shortfall separately. A month paid in full cannot, which is what stops the doubles. */}
          {openedRow?.status === 'short' && (
            <p className="flex flex-wrap items-center gap-2 text-amber-800">
              {money(openedRow.shortBy)} short of the {money(openedRow.expected)} expected.
              <button type="button" className="btn-ghost btn-sm" onClick={() => { setMonth(null); setPay({ month: openedRow.month, receivedOn: isoToday(), amount: openedRow.shortBy, reference: '' }); }}>
                <Plus size={13} /> Record the shortfall
              </button>
            </p>
          )}
        </div>
      )}
      {pay && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-2">
          <span className="text-slate-700 mb-2">Payment for {mon(pay.month)}</span>
          <Field label="Received on">{(id) => <input id={id} type="date" className="input-field !w-auto" value={pay.receivedOn} onChange={(e) => setPay((p) => ({ ...p, receivedOn: e.target.value }))} />}</Field>
          <Field label="Amount ($)">{(id) => <input id={id} type="number" min="0" step="0.01" className="input-field !w-32" value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} />}</Field>
          <Field label="Remittance reference">{(id) => <input id={id} className="input-field !w-40" value={pay.reference} onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))} />}</Field>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setPay(null)}>Cancel</button>
          <button type="button" className="btn-primary btn-sm" onClick={savePay}><Plus size={13} /> Record</button>
        </div>
      )}
      {q.length > 0 && (
        <div>
          <p className="font-medium text-slate-800">Quarter checks</p>
          {q.map((x) => (
            <p key={x.quarter} className="text-[12.5px] text-slate-600">
              To {fmtDate(x.end)} ({x.expectedPct}% of the year): {x.services.length ? x.services.map((s) => (
                <span key={s.name} className={s.status === 'behind' ? 'text-red-800' : s.status === 'ahead' ? 'text-teal-800' : ''}>{s.name} {s.deliveredPct}%{s.status === 'behind' ? ' (behind)' : ''}; </span>
              )) : 'no targets set'}
            </p>
          ))}
        </div>
      )}
      <p className="text-[12.5px] text-slate-600">
        Year to date: delivered value {money(y.deliveredValue)} against {money(y.received)} received
        {y.unspentEstimate > 0 ? <span className="text-amber-800"> · {money(y.unspentEstimate)} received but not yet delivered against (unspent funds are recovered after the year)</span> : null}.
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
        <span className="text-[12.5px] text-slate-700"><FileText size={13} className="inline -mt-0.5" /> Annual financial declaration for {d.fy.label}: due {fmtDate(d.declaration.due)}
          {d.declaration.submitted_on ? <span className="text-emerald-700"> · submitted {fmtDate(d.declaration.submitted_on)}{d.declaration.reference ? ` (${d.declaration.reference})` : ''}</span> : ' · not submitted'}</span>
        {/* Opens on what was recorded, so correcting the reference does not mean typing the date again. */}
        {!decl && (
          <button type="button" className="btn-ghost btn-sm" onClick={() => setDecl({ fy, on: d.declaration.submitted_on || isoToday(), ref: d.declaration.reference || '' })}>
            {d.declaration.submitted_on ? 'Change the submission' : 'Record submission'}
          </button>
        )}
      </div>
      {decl && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-2">
          <Field label="Submitted on">{(id) => <input id={id} type="date" className="input-field !w-auto" value={decl.on} onChange={(e) => setDecl((p) => ({ ...p, on: e.target.value }))} />}</Field>
          <Field label="Reference">{(id) => <input id={id} className="input-field !w-48" value={decl.ref} onChange={(e) => setDecl((p) => ({ ...p, ref: e.target.value }))} />}</Field>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setDecl(null)}>Cancel</button>
          <button type="button" className="btn-primary btn-sm" onClick={saveDecl}>Save</button>
        </div>
      )}
      {(twice.visits.length > 0 || twice.overlaps.length > 0) && (
        <div className="rounded-lg border border-red-200 bg-red-50/40 p-2 space-y-1">
          <p className="font-medium text-red-800"><AlertTriangle size={13} className="inline -mt-0.5" /> Funded twice</p>
          {twice.visits.slice(0, 20).map((v) => <p key={v.shift_id} className="text-[12.5px] text-red-800">{fmtDate(v.date)} {v.name}, {v.service}: drawn {money(v.sah_cost)} from Support at Home while enrolled in CHSP</p>)}
          {twice.overlaps.slice(0, 20).map((o) => <p key={o.enrolment_id} className="text-[12.5px] text-amber-800">{o.name}: Support at Home from {fmtDate(o.sah_start)} while CHSP runs {o.chsp_end ? `to ${fmtDate(o.chsp_end)}` : 'open'}. Use Move to Support at Home.</p>)}
        </div>
      )}
    </Panel>
  );
}

// The provider's contribution policy versions; each enrolment records the one it started under.
// A version can be corrected (a wrong date or a broken link) without adding a new one, so the
// enrolments recorded under it keep pointing at the same version.
export function ChspPolicies() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [f, setF] = useState(null);
  const load = useCallback(() => {
    setError(null);
    api.get('/aged-care/chsp/policies').then((r) => setRows(r.data || []))
      .catch((e) => { setRows([]); setError(e.response?.data?.message || 'Could not load the policy versions'); });
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    const body = { version: f.version, effectiveFrom: f.effectiveFrom, summary: f.summary, documentUrl: f.documentUrl };
    try {
      if (f.id) await api.put(`/aged-care/chsp/policies/${f.id}`, body);
      else await api.post('/aged-care/chsp/policies', body);
      toast.success(f.id ? 'Policy version saved' : 'Policy version added'); setF(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
  };
  const edit = (r) => setF({ id: r.id, version: r.version || '', effectiveFrom: r.effective_from || '', summary: r.summary || '', documentUrl: r.document_url || '' });
  return (
    <Panel title="Contribution policy versions" icon={FileText} className="mt-5" bodyClass="px-4 py-3 space-y-2 text-[13px]"
      actions={!f && !error && <button type="button" className="btn-ghost btn-sm" onClick={() => setF({ version: '', effectiveFrom: isoToday(), summary: '', documentUrl: '' })}><Plus size={13} /> New version</button>}>
      <p className="text-[12px] text-slate-500">Your published client contribution policy under the national framework. Each enrolment records the version it started under; hardship decisions are made under it.</p>
      {error && (
        <p className="text-red-800" role="alert">{error} <button type="button" className="underline ml-1" onClick={load}>Try again</button></p>
      )}
      {!error && rows.length === 0 && !f && <p className="text-slate-500">No versions recorded yet.</p>}
      {rows.map((r) => (
        <div key={r.id} className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-slate-700 min-w-0">Version {r.version}, from {fmtDate(r.effective_from)} · {r.enrolments} enrolment{r.enrolments === 1 ? '' : 's'}
            {r.document_url ? <> · <a className="text-teal-700 underline" href={r.document_url} target="_blank" rel="noopener noreferrer">policy</a></> : null}{r.summary ? ` · ${r.summary}` : ''}</p>
          {f?.id !== r.id && <button type="button" className="btn-ghost btn-sm" onClick={() => edit(r)}><Pencil size={13} /> Edit</button>}
        </div>
      ))}
      {f && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <p className="text-[12.5px] font-medium text-slate-800">{f.id ? `Edit version ${f.version || ''}` : 'New version'}</p>
          <FormRow cols={2}>
            <Field label="Version">{(id) => <input id={id} className="input-field" value={f.version} onChange={(e) => setF((p) => ({ ...p, version: e.target.value }))} placeholder="2026.1" />}</Field>
            <Field label="In force from">{(id) => <input id={id} type="date" className="input-field" value={f.effectiveFrom} onChange={(e) => setF((p) => ({ ...p, effectiveFrom: e.target.value }))} />}</Field>
          </FormRow>
          <Field label="Link to the policy">{(id) => <input id={id} className="input-field" value={f.documentUrl} onChange={(e) => setF((p) => ({ ...p, documentUrl: e.target.value }))} placeholder="https://" />}</Field>
          <Field label="What changed">{(id) => <input id={id} className="input-field" value={f.summary} onChange={(e) => setF((p) => ({ ...p, summary: e.target.value }))} />}</Field>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost btn-sm" onClick={() => setF(null)}>Cancel</button>
            <button type="button" className="btn-primary btn-sm" onClick={save}>Save</button>
          </div>
        </div>
      )}
    </Panel>
  );
}
